import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import * as turf from "@turf/turf";

import { getAreaRiskAssessment } from "../../../shared/api";
import {
  DRAW_MODES,
  MAX_TOTAL_AREA_SQM,
  cloneFeatureCollection,
  getGeometryKey,
  getPolygonCentroid,
  isPolygon,
} from "./helpers";
import { useGeometryMeasure } from "./useGeometryMeasure";

const EMPTY_FC = { type: "FeatureCollection", features: [] };
const DEFAULT_FLOOR = "ΙΣΟΓΕΙΟ";

/**
 * Owns all polygon-drawing state. The MapboxDraw instance is registered by
 * <DrawControl>; the toolbar and the side-menu tab consume this state through
 * <PolygonDrawProvider>.
 */
export function usePolygonDraw() {
  const { i18n } = useTranslation();
  const drawRef = useRef(null);
  // Last committed snapshot, used as the "previous" state for the undo stack.
  const currentSnapshotRef = useRef(EMPTY_FC);
  // Guards programmatic draw.set() during undo/redo from being recorded.
  const isRestoringRef = useRef(false);

  const [features, setFeatures] = useState(EMPTY_FC);
  const [mode, setModeState] = useState(DRAW_MODES.SELECT);
  const [selectedIds, setSelectedIds] = useState([]);
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const [floor, setFloor] = useState(DEFAULT_FLOOR);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [results, setResults] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [isActive, setIsActive] = useState(false);

  // Server-side measurement of the drawn polygons — re-runs on every
  // create / edit / delete via the `features` snapshot.
  const {
    measuredById,
    measureData,
    totalArea,
    totalPerimeter,
    isMeasuring,
    measureError,
  } = useGeometryMeasure(features);

  const refreshFeatures = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return EMPTY_FC;
    const fc = cloneFeatureCollection(draw.getAll());
    setFeatures(fc);
    return fc;
  }, []);

  // Called by <DrawControl> once the instance exists.
  const registerDraw = useCallback(
    (draw) => {
      drawRef.current = draw;
      currentSnapshotRef.current = cloneFeatureCollection(draw.getAll());
      refreshFeatures();
    },
    [refreshFeatures],
  );

  const unregisterDraw = useCallback(() => {
    drawRef.current = null;
  }, []);

  // Fired on draw.create / draw.update / draw.delete.
  const onDrawChange = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return;
    if (isRestoringRef.current) {
      refreshFeatures();
      return;
    }
    const previous = currentSnapshotRef.current;
    const next = cloneFeatureCollection(draw.getAll());
    setUndoStack((stack) => [...stack, previous]);
    setRedoStack([]);
    currentSnapshotRef.current = next;
    setFeatures(next);
  }, [refreshFeatures]);

  const onModeChange = useCallback((e) => {
    if (e?.mode) setModeState(e.mode);
  }, []);

  const onSelectionChange = useCallback((e) => {
    setSelectedIds((e?.features || []).map((f) => f.id));
  }, []);

  const setMode = useCallback(
    (nextMode, options) => {
      const draw = drawRef.current;
      if (!draw) return;
      draw.changeMode(nextMode, options);
      setModeState(nextMode);
    },
    [],
  );

  const startDrawing = useCallback(() => setMode(DRAW_MODES.DRAW), [setMode]);
  const startSelecting = useCallback(() => setMode(DRAW_MODES.SELECT), [setMode]);

  const startEditing = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return;
    // Read the selection straight from draw — React state can lag the actual
    // mapbox-gl-draw selection, and direct_select throws without a featureId.
    const ids = draw.getSelectedIds();
    if (ids.length !== 1) return;
    setMode(DRAW_MODES.EDIT, { featureId: ids[0] });
  }, [setMode]);

  const deleteSelected = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return;
    draw.trash();
  }, []);

  const applySnapshot = useCallback(
    (snapshot) => {
      const draw = drawRef.current;
      if (!draw) return;
      const clone = cloneFeatureCollection(snapshot);
      isRestoringRef.current = true;
      draw.set(clone);
      isRestoringRef.current = false;
      currentSnapshotRef.current = clone;
      setFeatures(clone);
    },
    [],
  );

  const undo = useCallback(() => {
    setUndoStack((stack) => {
      if (!stack.length) return stack;
      const previous = stack[stack.length - 1];
      setRedoStack((r) => [...r, currentSnapshotRef.current]);
      applySnapshot(previous);
      return stack.slice(0, -1);
    });
  }, [applySnapshot]);

  const redo = useCallback(() => {
    setRedoStack((stack) => {
      if (!stack.length) return stack;
      const next = stack[stack.length - 1];
      setUndoStack((u) => [...u, currentSnapshotRef.current]);
      applySnapshot(next);
      return stack.slice(0, -1);
    });
  }, [applySnapshot]);

  // Back to a blank slate: no polygons, no history, select mode.
  const resetDrawingState = useCallback(() => {
    const draw = drawRef.current;
    if (draw) {
      isRestoringRef.current = true;
      draw.deleteAll();
      draw.changeMode(DRAW_MODES.SELECT);
      isRestoringRef.current = false;
    }

    currentSnapshotRef.current = EMPTY_FC;
    setFeatures(EMPTY_FC);
    setUndoStack([]);
    setRedoStack([]);
    setModeState(DRAW_MODES.SELECT);
    setSelectedIds([]);
  }, []);

  // "New assessment" — clear the results and start over with a clean map.
  const clearResults = useCallback(() => {
    setResults(null);
    setSubmitError("");
    setName("");
    setNotes("");
    resetDrawingState();
  }, [resetDrawingState]);

  // "Adjust & re-assess" — drop the results and return to the input form,
  // keeping the drawn polygons so they can be edited and re-submitted.
  const reviseAssessment = useCallback(() => {
    setResults(null);
    setSubmitError("");
  }, []);

  // Mirror the point-assessment behavior: switching away from the area tab
  // clears everything — drawn polygons, undo/redo history, and results.
  useEffect(() => {
    if (isActive) return;
    resetDrawingState();
    setResults(null);
    setSubmitError("");
    setName("");
    setNotes("");
  }, [isActive, resetDrawingState]);

  // Lock the drawn polygons while a result is shown: switch draw into the
  // non-interactive STATIC mode so nothing can be selected/moved/edited. When
  // the result is cleared (e.g. "Adjust & re-assess"), return to select mode so
  // the polygons become editable again.
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw || !isActive) return;
    const nextMode = results ? DRAW_MODES.STATIC : DRAW_MODES.SELECT;
    draw.changeMode(nextMode);
    setModeState(nextMode);
    if (results) setSelectedIds([]);
  }, [results, isActive]);

  // Per-polygon metrics: centroid from turf (label placement), area/perimeter
  // from the last server measurement. A polygon whose geometry no longer
  // matches its measured entry is either measuring (skeleton) or errored.
  const polygonMetrics = useMemo(() => {
    const polygons = (features?.features || []).filter(isPolygon);
    return polygons.map((feature) => {
      const entry = measuredById.get(feature.id);
      const measured =
        entry && entry.geometryKey === getGeometryKey(feature.geometry);
      return {
        id: feature.id,
        centroid: getPolygonCentroid(feature),
        area: measured ? entry.area : undefined,
        perimeter: measured ? entry.perimeter : undefined,
        isMeasuring: !measured && isMeasuring,
        hasError: !measured && !isMeasuring && !!measureError,
      };
    });
  }, [features, measuredById, isMeasuring, measureError]);

  const polygonCount = polygonMetrics.length;

  const isOverAreaLimit = (totalArea ?? 0) > MAX_TOTAL_AREA_SQM;

  const sendAssessment = useCallback(async () => {
    // Submission requires a settled server measurement — the button is also
    // disabled while measuring or on a measurement error.
    if (isMeasuring || measureError) return;
    if (!measureData?.polygons?.features?.length) return;

    // Block submission when the total area exceeds the configured limit.
    if ((measureData.area_sqm ?? 0) > MAX_TOTAL_AREA_SQM) return;

    // The measure response already carries each polygon's `area_sqm` /
    // `perimeter_m` in its feature `properties`, so the server can persist
    // them per-polygon alongside the geometry.
    const polygons = cloneFeatureCollection(measureData.polygons);

    // Combined centroid — used as the assessment point (PDF snapshot camera).
    let centroid = null;
    try {
      centroid = turf.centroid(polygons).geometry.coordinates;
    } catch {
      centroid = null;
    }

    const trimmedName = name.trim() || undefined;
    const trimmedNotes = notes.trim() || undefined;

    try {
      setIsSubmitting(true);
      setSubmitError("");
      const data = await getAreaRiskAssessment({
        floor,
        language: i18n.language,
        polygons,
        name: trimmedName,
        notes: trimmedNotes,
      });
      // Store a PDF/history-ready assessment: the risk data merged with the
      // submitted geometry, totals, centroid and floor. `RiskItem` reads
      // fire/flood/earthquake from it, and the PDF export reads the rest.
      setResults({
        ...data,
        assessment_type: "area",
        floor,
        name: data?.name ?? trimmedName,
        notes: data?.notes ?? trimmedNotes,
        geometry: polygons,
        area_sqm: data?.area_sqm ?? measureData.area_sqm,
        perimeter_m: data?.perimeter_m ?? measureData.perimeter_m,
        latitude: data?.latitude ?? centroid?.[1],
        longitude: data?.longitude ?? centroid?.[0],
        creation_time: data?.creation_time || new Date().toISOString(),
      });
    } catch (error) {
      setSubmitError(
        error?.error_code === "invalid_geometry"
          ? "errors.invalidGeometry"
          : "errors.fetchRiskData",
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [measureData, isMeasuring, measureError, floor, name, notes, i18n.language]);

  return {
    // instance wiring
    drawRef,
    registerDraw,
    unregisterDraw,
    onDrawChange,
    onModeChange,
    onSelectionChange,
    // drawing state
    features,
    polygonMetrics,
    polygonCount,
    totalArea,
    totalPerimeter,
    isMeasuring,
    measureError,
    isOverAreaLimit,
    maxTotalArea: MAX_TOTAL_AREA_SQM,
    mode,
    selectedIds,
    // mode actions
    setMode,
    startDrawing,
    startSelecting,
    startEditing,
    deleteSelected,
    // history
    undo,
    redo,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    // floor + submission
    floor,
    setFloor,
    name,
    setName,
    notes,
    setNotes,
    results,
    isSubmitting,
    submitError,
    sendAssessment,
    clearResults,
    reviseAssessment,
    // activation
    isActive,
    setIsActive,
  };
}
